import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import _ from 'lodash';
import objectSupport from 'dayjs/plugin/objectSupport';
import randomString from 'random-string';

import PropTypes from 'prop-types';

import {
    ProviderHeader,
    GoBackButton,
    Tag,
    Text,
    ModeratorCard,
    SessionDetailCard,
    PatientReadyCard,
    NoInformation,
    RingingDialog,
    SkipQueueDialog,
    NoCallDialog,
    PDPADialog,
} from '../../components/index';

import EditSessionDialog from '../../components/Provider/Dialog/EditSessionDialog';

import { providerPageWidth, userRole } from '../../constant';

import {
    GET_SESSION_DETAL_MEET,
    skipQueue,
    canStart,
    updateRoomId,
    sendNotificationSession,
} from '../../services/provider';

import { createRoom, joinRoom } from '../../services';

import { setMessagesReceiver, alertError, alertSuccess } from '../../redux/stateActions';

import socket from '../../socket';

const ringtone = '/resources/audios/providerRingtone.mp3';

import * as cookiesManager from '../../utils/cookiesManager';

dayjs.extend(objectSupport);

const OneOnOneSessionDetail = ({ history, location }) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const [audio] = useState(new Audio(ringtone));

    const [loading, setLoading] = useState(false);

    /*--------------- Call Control Controller ---------------*/
    const [micOn, setMicOn] = useState(true);
    const [cameraOn, setCameraOn] = useState(true);

    useEffect(() => {
        setMicOn(true);
        setCameraOn(true);
        cookiesManager.setDevices({ micEnabled: true, webcamEnabled: true });
    }, []);

    const handleMic = () => {
        const devicesCookie = cookiesManager.getDevices();
        setMicOn(!micOn);
        cookiesManager.setDevices({ ...devicesCookie, micEnabled: !micOn });
    };

    const handleCamera = () => {
        const devicesCookie = cookiesManager.getDevices();
        setCameraOn(!cameraOn);
        cookiesManager.setDevices({ ...devicesCookie, webcamEnabled: !cameraOn });
    };

    const [participants, setParticipants] = useState([]);
    const [selectedSession, setSelectedSession] = useState({});
    const [switchState, setSwitchState] = useState({
        ready: false,
        speakToText: false,
        soundRecord: false,
    });

    const handleGoBack = () => {
        if (location.state?.prevPath) {
            const path = {
                pathname: location.state.prevPath,
                state: {
                    tab: location.state.prevTab,
                },
            };
            history.push(path);
        } else {
            history.goBack();
        }
    };

    const handleSwitchChange = (event) => {
        const name = event.target.name;
        const checked = event.target.checked;
        setSwitchState({ ...switchState, [name]: checked });
    };

    /*--------------- Get Meet ---------------*/
    const [meetData, setMeetData] = useState({});
    const [meetEnd, setMeetEnd] = useState(false);

    const {
        data: getMeetData,
        error: getMeetError,
        refetch: getMeetRefetch,
    } = useQuery(GET_SESSION_DETAL_MEET, {
        variables: { id: id },
        skip: !id,
        pollInterval: 30000,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getMeetError) {
            dispatch(alertError(`Cannot get Meet data: ${getMeetError.message}`));
        }

        if (getMeetData && _.isEmpty(getMeetData.meet)) {
            dispatch(alertError(`Meet not found`));
            handleGoBack();
        }

        if (getMeetData && !_.isEmpty(getMeetData.meet)) {
            setMeetData(getMeetData.meet);

            const sessions = getMeetData.meet.sessions;

            const newParticipants = sessions.map((session) => ({
                queue: null,
                patientSummary: session.patient_summaries[0],
                patient: session.patient_summaries[0].patient,
                ...session,
            }));

            const checkedInPatient = sessions
                .filter((session) => session.patient_summaries[0].checkinTime !== null)
                .sort((a, b) =>
                    dayjs(a.patient_summaries[0].checkinTime).isAfter(
                        dayjs(b.patient_summaries[0].checkinTime)
                    )
                        ? 1
                        : -1
                );

            checkedInPatient.forEach((item, index) => {
                const findIndex = newParticipants.findIndex((p) => p.id === item.id);

                newParticipants[findIndex].queue = (index + 1).toLocaleString('en-US', {
                    minimumIntegerDigits: 2,
                });
                // newParticipants[findIndex].queue = (index + 1)
            });

            const sortedNewParticipants = _.orderBy(
                newParticipants,
                [
                    'queue',
                    (obj) =>
                        dayjs().set({
                            hour: obj.startTime.split(':')[0],
                            minute: obj.startTime.split(':')[1],
                        }),
                ],
                ['asc', 'asc']
            );

            setParticipants(sortedNewParticipants);
        }
    }, [getMeetError, getMeetData]);

    useEffect(() => {
        if (!_.isEmpty(meetData)) {
            if (meetData.cancelled) {
                return setMeetEnd(true);
            }

            const checkTime = () => {
                const endTime = dayjs(meetData.startDate).set({
                    hour: meetData.endTime.split(':')[0],
                    minute: meetData.endTime.split(':')[1],
                });

                if (dayjs().isAfter(endTime, 'minute')) {
                    setMeetEnd(true);
                    clearInterval(myInterval);
                }
            };

            const myInterval = setInterval(checkTime, 1000);
        }
    }, [meetData]);

    const [openRingingDialog, setOpenRingingDialog] = useState(false);
    const [openSkipQueueDialog, setOpenSkipQueueDialog] = useState(false);

    const handleMessage = (patient) => {
        dispatch(
            setMessagesReceiver({
                id: patient?.id,
                avatar: patient?.user?.avatar?.url,
                name: patient?.user?.fullname,
                isGroup: false,
            })
        );
    };

    /*--------------- PDPA ---------------*/
    const [openPDPA, setOpenPDPA] = useState({});
    const [accepted, setAccepted] = useState(false);

    const onCheckChange = (e) => {
        setAccepted(e.target.checked);
    };

    const handleClosePDPA = () => {
        setOpenPDPA({});
        setAccepted(false);
    };

    const onAccept = () => {
        handleCall(openPDPA);
        setOpenPDPA({});
    };

    const handleCall = async (session) => {
        if (!accepted) {
            setOpenPDPA(session);
            return;
        }

        setLoading(session.id);

        try {
            if (!session.roomId) {
                const res = await canStart({ id: user.staff.hospital.id, amount: 100 });
                if (res.status === 200) {
                    if (res.data.canStart === false) {
                        setLoading(false);
                        return dispatch(alertError(`Not enough credit`));
                    }
                } else {
                    throw new Error(res);
                }
            }
            socket
                .timeout(6000)
                .emit(
                    'checkPaitentOnline',
                    { patientId: session.patient.id, staffId: user.staff.id },
                    (err, response) => {
                        if (err) {
                            // the other side did not acknowledge the event in the given delay
                            setNoCallDialogType('OFFLINE');
                        }
                        if (response) {
                            if (_.isEmpty(response) || response === 'error') {
                                setNoCallDialogType('OFFLINE');
                                return;
                            }
                            if (response[0] === 'ok') {
                                setSelectedSession(session);
                                socket.emit('providerCalling', {
                                    patientUserId: session.patient?.user?.id,
                                    patientId: session?.patient?.id,
                                    staffId: user?.staff?.id,
                                    staffUserId: user.id,
                                    sessionId: session.id,
                                    docterUserId: user.id,
                                });
                            }
                        }
                    }
                );
            setLoading(false);
        } catch (error) {
            setLoading(false);
            dispatch(alertError(`Call Error: ${error.response?.data?.message}`));
        }
    };

    const handleCancelCall = () => {
        socket.emit('providerCancelCall', {
            sessionId: selectedSession.id,
            patientId: selectedSession.patient.id,
        });
        clearData();
    };

    const handleJoinRoom = async (roomId) => {
        const params = new URLSearchParams();
        params.append('roomId', roomId);
        params.append('sessionId', randomString({ length: 16 }).toLowerCase());
        params.append('role', userRole.DOCTOR);
        params.append(
            'displayName',
            `${user.fullname}|${user.staff?.id}|${user.avatar?.url}|Provider`
        );
        params.append('password', '01011999');

        const joinRes = await joinRoom(roomId, params);

        return joinRes;
    };

    const handleSetUpRoom = async (sessionId) => {
        try {
            const result = await createRoom();

            if (result.roomId) {
                const joinRes = await handleJoinRoom(result.roomId);

                if (joinRes.data) {
                    const updateRoomIdRes = await updateRoomId({
                        sessionId,
                        roomId: result.roomId,
                    });

                    if (updateRoomIdRes.status === 200) {
                        socket.emit('providerSetUpRoom', {
                            patientId: selectedSession.patient.id,
                            roomId: result.roomId,
                            sessionId: sessionId,
                        });

                        setOpenRingingDialog(false);

                        window.open(
                            `${window.location.origin}/provider/call/${sessionId}?roomId=${result.roomId}&session=${joinRes.data.session}`,
                            '_blank'
                        );
                    }
                } else {
                    throw new Error(joinRes);
                }
            }
        } catch (error) {
            dispatch(alertError(`handleSetUpRoom Error: ${error.message}`));
            clearData();
        }
    };

    const handleSkip = () => {
        setOpenSkipQueueDialog(true);
    };

    const handleCancelSkipQueue = () => {
        setSelectedSession({});
        setOpenSkipQueueDialog(false);
    };

    const clearData = () => {
        setOpenSkipQueueDialog(false);
        setOpenRingingDialog(false);
        setSelectedSession({});
    };

    const handleConfirmSkipQueue = async (remark) => {
        setOpenSkipQueueDialog(false);

        try {
            const res = await skipQueue({
                patientSummaryId: selectedSession.patientSummary.id,
                sessionId: selectedSession.id,
                patientId: selectedSession.patient.id,
                staffId: user.staff.id,
                remark: remark,
            });

            if (res.status === 200) {
                clearData();
                getMeetRefetch();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`Cannot Skip queue Error: ${error}`));
        }
    };

    useEffect(() => {
        socket.on('patientCheckedIn', (data) => {
            if (data.meetId === id) {
                getMeetRefetch();
            }
        });
        socket.on('sessionEnd', (data) => {
            if (data.meetId === id) {
                getMeetRefetch();
            }
        });
        socket.on('meetUpdated', (data) => {
            console.log('meetUpdated', data);
            if (data.meetId === id) {
                getMeetRefetch();
            }
        });
    }, []);

    useEffect(() => {
        // if socket is disconnected while ringing dialog is open
        socket.on('disconnect', () => {
            console.log('disconnect');
            clearData();
        });

        socket.on('patientReceivedCall', () => {
            setOpenRingingDialog(true);
        });

        socket.on('patientRejectCall', () => {
            clearData();
            setNoCallDialogType('REJECT');
        });

        socket.on('patientInAnotherCall', () => {
            clearData();
            setNoCallDialogType('ANOTHOR_CALL');
        });
    }, []);

    useEffect(() => {
        let pingInterval;
        let count = 0;

        if (openRingingDialog) {
            socket.on('pingTarget', (data, callback) => {
                callback('ok');
            });

            if (!_.isEmpty(selectedSession)) {
                pingInterval = setInterval(() => {
                    if (count >= 5) {
                        setNoCallDialogType('OFFLINE');
                        clearData();
                        clearInterval(pingInterval);
                        pingInterval = 0;
                        count = 0;
                    }

                    socket
                        .timeout(3000)
                        .emit('pingTarget', { id: selectedSession.patient.id }, (err, response) => {
                            if (err) {
                                count++;
                            }
                            if (response) {
                                if (_.isEmpty(response) || response === 'error') {
                                    count++;
                                    return;
                                }
                                if (response[0] === 'ok') {
                                    count = 0;
                                }
                            }
                        });
                }, 1000);
            }
        }

        if (!openRingingDialog) {
            clearInterval(pingInterval);
            pingInterval = 0;
            count = 0;
        }
    }, [openRingingDialog, selectedSession]);

    useEffect(() => {
        socket.on('patientAlreadyInRoom', async (data) => {
            try {
                const joinRes = await handleJoinRoom(data.roomId);

                setOpenRingingDialog(false);

                if (joinRes.data) {
                    window.open(
                        `${window.location.origin}/provider/call/${data.sessionId}?roomId=${data.roomId}&session=${joinRes.data.session}`,
                        '_blank'
                    );
                } else {
                    throw new Error(joinRes);
                }
            } catch (error) {
                dispatch(alertError(`Cannot join room`));
            }
        });
    }, [micOn, cameraOn]);

    useEffect(() => {
        if (!_.isEmpty(selectedSession)) {
            socket.off('patientAnswerCall');
            socket.on('patientAnswerCall', (data, callback) => {
                callback('ok');
                setOpenSkipQueueDialog(false);
                handleSetUpRoom(data.sessionId);
            });
        }
    }, [selectedSession]);

    useEffect(() => {
        openRingingDialog ? audio.play() : audio.pause();
        audio.volume = 0.5;
        audio.loop = true;
    }, [openRingingDialog]);

    /*---------- Dialog ----------*/
    const [noCallDialogType, setNoCallDialogType] = useState('');
    const handleCloseNoCallDialog = () => {
        setNoCallDialogType('');
    };

    /*---------- Send ----------*/
    const handleNotification = async (patientSummary) => {
        try {
            const res = await sendNotificationSession({ patientSummaryID: patientSummary.id });

            if (res.status === 200) {
                dispatch(alertSuccess('Notification sent'));
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`Cannot send notification: ${error}`));
        }
    };

    const handleDuplicate = (id, type) => {
        history.push(`/provider/calendar/${type}/duplicate/${id}`);
    };

    const handleSessionDetail = (id, type) => {
        history.push(`/provider/calendar/${type}/edit/${id}`);
    };

    return (
        <div
            style={{
                padding: '15px 20px 50px',
                maxWidth: providerPageWidth,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '15px',
                position: 'relative',
            }}
        >
            <PDPADialog
                open={!_.isEmpty(openPDPA)}
                onClose={handleClosePDPA}
                accepted={accepted}
                onCheckChange={onCheckChange}
                onAccept={onAccept}
            />
            <NoCallDialog
                open={Boolean(noCallDialogType)}
                onClose={handleCloseNoCallDialog}
                type={noCallDialogType}
            />

            <RingingDialog
                open={openRingingDialog}
                data={selectedSession?.patient}
                onClose={handleCancelCall}
                onSkip={handleSkip}
                $desktopLayout={true}
                $providerLayout={true}
                micOn={micOn}
                handleMic={handleMic}
                cameraOn={cameraOn}
                handleCamera={handleCamera}
            />
            <SkipQueueDialog
                open={openSkipQueueDialog}
                onConfirm={handleConfirmSkipQueue}
                onCancel={handleCancelSkipQueue}
            />
            <ProviderHeader>
                <GoBackButton $text onClick={handleGoBack} />
            </ProviderHeader>
            <div style={{ display: 'flex', alignItems: 'center', columnGap: '20px' }}>
                <Tag $backgroundColor="pink">
                    <Text $fontSize="10px" $color="white">
                        1 ON 1 SESSION
                    </Text>
                </Tag>
                <Text $fontSize="24px" $bold>
                    Activity name - {meetData?.name}
                </Text>
            </div>
            <div style={{ display: 'grid', columnGap: '20px', gridTemplateColumns: 'auto 300px' }}>
                <SessionDetailCard
                    data={meetData}
                    onEdit={() => handleSessionDetail(meetData.id, meetData.type)}
                    disabled={meetEnd}
                    onDuplicate={() => handleDuplicate(meetData.id, meetData.type)}
                />
                <ModeratorCard
                    data={meetData}
                    switchState={switchState}
                    onSwitchChange={handleSwitchChange}
                    disabled={meetEnd}
                />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', rowGap: '15px' }}>
                <Text $fontSize="18px" $bold>
                    Member
                </Text>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: _.isEmpty(participants) ? '1fr' : 'repeat(4,1fr)',
                        columnGap: '20px',
                        rowGap: '20px',
                    }}
                >
                    {_.isEmpty(participants) ? (
                        <NoInformation />
                    ) : (
                        participants.map((item) => (
                            <PatientReadyCard
                                key={item.id}
                                data={item}
                                onSkip={() => {
                                    setSelectedSession(item);
                                    handleSkip();
                                }}
                                onMessage={() => handleMessage(item.patient)}
                                onCall={() => handleCall(item)}
                                onNotification={() => handleNotification(item.patientSummary)}
                                disabled={
                                    user?.staff?.id !== meetData?.staff?.id ||
                                    meetEnd ||
                                    (loading && loading !== item.id)
                                }
                                loading={loading === item.id}
                            />
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

OneOnOneSessionDetail.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
};

export default OneOnOneSessionDetail;
